"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.extractNetworkProtocol = extractNetworkProtocol;
exports.getBrowserPerformanceAPI = getBrowserPerformanceAPI;
exports.isMeasurementValue = isMeasurementValue;
exports.msToSec = msToSec;
exports.startAndEndSpan = startAndEndSpan;
exports.startStandaloneWebVitalSpan = startStandaloneWebVitalSpan;

var _core = require("@sentry/core");

var _types = require("../types.js");

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/**
 * Checks if a given value is a valid measurement value.
 */
function isMeasurementValue(value) {
  return typeof value === 'number' && isFinite(value);
}
/**
 * Helper function to start child on transactions. This function will make sure that the transaction will
 * use the start timestamp of the created child span if it is earlier than the transactions actual
 * start timestamp.
 */


function startAndEndSpan(parentSpan, startTimeInSeconds, endTime, _ref) {
  let ctx = _extends({}, _ref);

  const parentStartTime = (0, _core.spanToJSON)(parentSpan).start_timestamp;

  if (parentStartTime && parentStartTime > startTimeInSeconds) {
    // We can only do this for SentrySpans...
    if (typeof parentSpan.updateStartTime === 'function') {
      parentSpan.updateStartTime(startTimeInSeconds);
    }
  } // The return value only exists for tests


  return (0, _core.withActiveSpan)(parentSpan, () => {
    const span = (0, _core.startInactiveSpan)(_objectSpread({
      startTime: startTimeInSeconds
    }, ctx));

    if (span) {
      span.end(endTime);
    }

    return span;
  });
}
/**
 * Starts an inactive, standalone span used to send web vital values to Sentry.
 * DO NOT use this for arbitrary spans, as these spans require special handling
 * during ingestion to extract metrics.
 *
 * This function adds a bunch of attributes and data to the span that's shared
 * by all web vital standalone spans. However, you need to take care of adding
 * the actual web vital value as an event to the span. Also, you need to assign
 * a transaction name and some other values that are specific to the web vital.
 *
 * Ultimately, you also need to take care of ending the span to send it off.
 *
 * @param options
 *
 * @returns an inactive, standalone and NOT YET ended span
 */


function startStandaloneWebVitalSpan(options) {
  const client = (0, _core.getClient)();

  if (!client) {
    return;
  }

  const name = options.name,
        transaction = options.transaction,
        passedAttributes = options.attributes,
        startTime = options.startTime;

  const _client$getOptions = client.getOptions(),
        release = _client$getOptions.release,
        environment = _client$getOptions.environment; // We need to get the replay, user, and activeTransaction from the current scope
  // so that we can associate replay id, profile id, and a user display to the span


  const replay = client.getIntegrationByName('Replay');
  const replayId = replay && replay.getReplayId();
  const scope = (0, _core.getCurrentScope)();
  const user = scope.getUser();
  const userDisplay = user !== undefined ? user.email || user.id || user.ip_address : undefined;
  let profileId;

  try {
    // @ts-expect-error skip optional chaining to save bundle size with try catch
    profileId = scope.getScopeData().contexts.profile.profile_id;
  } catch (e) {// do nothing
  }

  const attributes = _objectSpread({
    release,
    environment,
    user: userDisplay || undefined,
    profile_id: profileId || undefined,
    replay_id: replayId || undefined,
    transaction,
    // Web vital score calculation relies on the user agent to account for different
    // browsers setting different thresholds for what is considered a good/meh/bad value.
    // For example: Chrome vs. Chrome Mobile
    'user_agent.original': _types.WINDOW.navigator && _types.WINDOW.navigator.userAgent
  }, passedAttributes);

  return (0, _core.startInactiveSpan)({
    name,
    attributes,
    startTime,
    experimental: {
      standalone: true
    }
  });
}
/** Get the browser performance API. */


function getBrowserPerformanceAPI() {
  // @ts-expect-error we want to make sure all of these are available, even if TS is sure they are
  return _types.WINDOW && _types.WINDOW.addEventListener && _types.WINDOW.performance;
}
/**
 * Converts from milliseconds to seconds
 * @param time time in ms
 */


function msToSec(time) {
  return time / 1000;
}
/**
 * Converts ALPN protocol ids to name and version.
 *
 * (https://www.iana.org/assignments/tls-extensiontype-values/tls-extensiontype-values.xhtml#alpn-protocol-ids)
 * @param nextHopProtocol PerformanceResourceTiming.nextHopProtocol
 */


function extractNetworkProtocol(nextHopProtocol) {
  let name = 'unknown';
  let version = 'unknown';
  let _name = '';

  for (const char of nextHopProtocol) {
    // http/1.1 etc.
    if (char === '/') {
      var _nextHopProtocol$spli = nextHopProtocol.split('/');

      var _nextHopProtocol$spli2 = _slicedToArray(_nextHopProtocol$spli, 2);

      name = _nextHopProtocol$spli2[0];
      version = _nextHopProtocol$spli2[1];
      break;
    } // h2, h3 etc.


    if (!isNaN(Number(char))) {
      name = _name === 'h' ? 'http' : _name;
      version = nextHopProtocol.split(_name)[1];
      break;
    }

    _name += char;
  }

  if (_name === nextHopProtocol) {
    // webrtc, ftp, etc.
    name = _name;
  }

  return {
    name,
    version
  };
}